/**
 * ProjectShell — sub-layout para rutas dentro de un proyecto.
 * `/projects/$projectId/*`
 *
 * Layout (h-full, flex column):
 *
 *   ┌─────────────────────────────────────────────┬──────────────┐
 *   │ Breadcrumb · ProjectHeader · ProjectTabs    │              │
 *   │ (shrink-0 — fijo arriba del área de trabajo)│              │
 *   ├─────────────────────────────────────────────┤ RightPanel   │
 *   │                                             │ (slot,       │
 *   │ <Outlet />                                  │  shrink-0,   │
 *   │ flex-1 min-h-0 — la ruta hija decide su     │  scroll      │
 *   │ scroll interno + opcional action bar.       │  interno)    │
 *   │                                             │              │
 *   └─────────────────────────────────────────────┴──────────────┘
 *
 * Esta arquitectura usa SOLO flexbox: nada de `position: fixed`, CSS
 * vars, ResizeObserver ni listeners de scroll. Si la altura del header
 * cambia o se agrega un footer del sistema, flex se recalcula solo.
 *
 * El project se carga aquí una sola vez con TanStack Query y se inyecta
 * vía context al outlet (cualquier vista interna lo recibe sin
 * duplicar fetch). El slot del panel derecho se expone vía
 * `useRightPanel(node)` en `./rightPanelSlot`.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import {
  Link,
  Outlet,
  useParams,
  useRouterState,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'

import { ApiError } from '../../api/client'
import { getProject, type Project } from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { MethodologyBadge } from '../../components/projects/MethodologyBadge'
import { ProjectStatusBadge } from '../../components/projects/ProjectStatusBadge'
import { useRightPanelLayoutStore } from '../../stores/rightPanelLayoutStore'
import { RightPanelProvider } from './rightPanelSlot'

// ─── Context para que las subvistas accedan al project sin re-fetch ───────
interface ProjectContextValue {
  project: Project
}

const ProjectContext = createContext<ProjectContextValue | null>(null)

export function useProjectContext(): ProjectContextValue {
  const ctx = useContext(ProjectContext)
  if (!ctx) {
    throw new Error('useProjectContext must be used within ProjectShell')
  }
  return ctx
}

// ─── Breadcrumb extension context ──────────────────────────────────────────
// Permite a vistas hijas (ej. editor de artifact) añadir segmentos
// adicionales al breadcrumb del shell — ej. "› Lifecycle › Brief de
// Descubrimiento". El shell renderea base + extensions.
export interface BreadcrumbSegment {
  label: string
}

interface BreadcrumbExtensionValue {
  setExtraSegments: (segments: BreadcrumbSegment[]) => void
}

const BreadcrumbExtensionContext =
  createContext<BreadcrumbExtensionValue | null>(null)

/**
 * Las vistas hijas usan este hook para registrar segmentos adicionales
 * que se mostrarán después del nombre del proyecto en el breadcrumb.
 * Limpia automáticamente al desmontar.
 */
export function useBreadcrumbExtension(segments: BreadcrumbSegment[]): void {
  const ctx = useContext(BreadcrumbExtensionContext)
  // Stringify para tener una dep estable (los arrays nuevos cada render
  // dispararían el effect en loop).
  const key = JSON.stringify(segments)
  useEffect(() => {
    if (!ctx) return
    ctx.setExtraSegments(segments)
    return () => {
      ctx.setExtraSegments([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

// ─── Component ─────────────────────────────────────────────────────────────
export function ProjectShell() {
  const { t } = useTranslation(['projects', 'errors'])
  const { projectId } = useParams({ from: '/projects/$projectId' })
  const { accessToken, loading: authLoading } = useAccessToken()

  const query = useQuery({
    queryKey: ['projects', projectId, 'detail'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getProject(accessToken, projectId)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })

  if (authLoading || query.isLoading) {
    return (
      <p className="font-mono text-xs text-ink-subtle">{t('shell.loading')}</p>
    )
  }
  if (query.error) {
    return <ErrorBox error={query.error} />
  }
  if (!query.data) return null

  const project = query.data

  return (
    <ProjectContext.Provider value={{ project }}>
      <ShellWithBreadcrumb project={project} />
    </ProjectContext.Provider>
  )
}

function ShellWithBreadcrumb({ project }: { project: Project }) {
  const [extraSegments, setExtraSegments] = useState<BreadcrumbSegment[]>([])
  // Host del portal del panel derecho. Las rutas hijas inyectan su
  // contenido vía `useRightPanel(node)` (ver `./rightPanelSlot`), que
  // usa `createPortal` para mover el JSX al DOM de este `<aside>`.
  // Usamos state (no ref puro) porque queremos un re-render cuando el
  // ref-callback recibe el nodo, así el contexto propaga el host
  // disponible a los consumidores.
  const [panelHost, setPanelHost] = useState<HTMLElement | null>(null)
  // Doc 21 Fase 10 — ancho del aside controlado por el user via drag
  // del divider izquierdo del RightSidePanel. El store persiste la
  // preferencia en localStorage y aplica el clamp [320, 720].
  const rightPanelWidth = useRightPanelLayoutStore((s) => s.width)

  return (
    <BreadcrumbExtensionContext.Provider value={{ setExtraSegments }}>
      <RightPanelProvider value={{ host: panelHost }}>
        {/* h-full = ocupa todo el <main>. flex-col = header arriba +
            cuerpo abajo. min-h-0 evita que flex-1 se desborde. */}
        <div className="h-full flex flex-col min-h-0 bg-canvas">
          {/* Header zone — shrink-0, NO se mueve con el scroll del
              cuerpo. Sin tricks de sticky/fixed: simplemente vive
              fuera del contenedor scrolleable. */}
          <div className="shrink-0">
            <Breadcrumb
              projectName={project.name}
              extraSegments={extraSegments}
            />
            <ProjectHeader project={project} />
            <ProjectTabs projectId={project.id} />
          </div>

          {/* Body zone — flex row: outlet (izquierda, scroll propio)
              + right panel host (derecha, siempre montado pero
              `empty:hidden` para sacarse del layout cuando no hay
              contenido). `min-h-0` aquí es CRÍTICO para que el flex-1
              no se desborde. */}
          <div className="flex-1 min-h-0 flex">
            <div className="flex-1 min-w-0 min-h-0 flex flex-col">
              <Outlet />
            </div>
            <aside
              ref={setPanelHost}
              aria-label="Project side panel"
              style={{ width: `${rightPanelWidth}px` }}
              className="shrink-0 border-l border-line bg-surface flex flex-col min-h-0 empty:hidden"
            />
          </div>
        </div>
      </RightPanelProvider>
    </BreadcrumbExtensionContext.Provider>
  )
}

// ─── Breadcrumb ────────────────────────────────────────────────────────────
function Breadcrumb({
  projectName,
  extraSegments,
}: {
  projectName: string
  extraSegments: BreadcrumbSegment[]
}) {
  const { t } = useTranslation('projects')
  const lastIdx = extraSegments.length - 1
  return (
    <nav className="flex items-center gap-1.5 px-8 pt-4 text-xs text-ink-muted flex-wrap">
      <Link to="/projects" className="hover:text-ink transition-colors">
        {t('shell.breadcrumb_root')}
      </Link>
      <ChevronRight className="w-3 h-3" />
      <span
        className={
          extraSegments.length === 0
            ? 'text-ink font-medium truncate'
            : 'text-ink-muted truncate'
        }
      >
        {projectName}
      </span>
      {extraSegments.map((seg, idx) => (
        <span key={idx} className="flex items-center gap-1.5">
          <ChevronRight className="w-3 h-3" />
          <span
            className={
              idx === lastIdx
                ? 'text-ink font-medium truncate'
                : 'text-ink-muted truncate'
            }
          >
            {seg.label}
          </span>
        </span>
      ))}
    </nav>
  )
}

// ─── Project header ────────────────────────────────────────────────────────
function ProjectHeader({ project }: { project: Project }) {
  const { t } = useTranslation('projects')
  return (
    <div className="bg-surface border-b border-line px-8 pt-4 pb-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[10px] font-mono text-ink-subtle">
              {project.code}
            </span>
            <ProjectStatusBadge status={project.status} />
            <MethodologyBadge methodology={project.methodology} />
          </div>
          <h1 className="font-display text-2xl text-ink truncate">
            {project.name}
          </h1>
          <p className="mt-1 font-body text-sm text-ink-muted">
            {project.client_name ? (
              <>
                <span className="font-medium">{t('shell.client_label')}:</span>{' '}
                {project.client_name}
              </>
            ) : (
              <span className="text-ink-subtle italic">—</span>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Tabs ──────────────────────────────────────────────────────────────────
type TabKey =
  | 'overview'
  | 'lifecycle'
  | 'official'
  | 'sources'
  | 'change_requests'
  | 'risks'
  | 'graph'
  | 'team'
  | 'usage'
  | 'activity'

interface TabDef {
  key: TabKey
  /** True = ruta real implementada. False = disabled visual con tooltip. */
  enabled: boolean
  /** Path relativo al proyecto. Solo rutas REGISTRADAS hoy aceptadas por
   * el tipo (TanStack Router las valida). */
  to?:
    | '/projects/$projectId/overview'
    | '/projects/$projectId/lifecycle'
    | '/projects/$projectId/team'
    | '/projects/$projectId/official'
    | '/projects/$projectId/sources'
    | '/projects/$projectId/change-requests'
    | '/projects/$projectId/usage'
  /** Marca visual `v2` en el tab. */
  v2?: boolean
}

const TABS: TabDef[] = [
  { key: 'overview', enabled: true, to: '/projects/$projectId/overview' },
  { key: 'lifecycle', enabled: true, to: '/projects/$projectId/lifecycle' },
  { key: 'official', enabled: true, to: '/projects/$projectId/official' },
  { key: 'sources', enabled: true, to: '/projects/$projectId/sources' },
  {
    key: 'change_requests',
    enabled: true,
    to: '/projects/$projectId/change-requests',
  },
  { key: 'risks', enabled: false, v2: true },
  { key: 'graph', enabled: false }, // pending UX definition
  { key: 'team', enabled: true, to: '/projects/$projectId/team' },
  { key: 'usage', enabled: true, to: '/projects/$projectId/usage' },
  { key: 'activity', enabled: false }, // pending UX definition
]

function ProjectTabs({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects')
  const { location } = useRouterState()
  const path = location.pathname

  return (
    <nav
      role="tablist"
      className="flex items-center gap-0 border-b border-line bg-surface px-8 overflow-x-auto"
    >
      {TABS.map((tab) => {
        const label = t(`shell.tabs.${tab.key}` as const)
        if (!tab.enabled || !tab.to) {
          return (
            <span
              key={tab.key}
              role="tab"
              aria-disabled="true"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-ink-subtle cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap"
              title={tab.v2 ? `${label} · v2` : label}
            >
              {label}
              {tab.v2 ? (
                <span className="text-[10px] font-mono">v2</span>
              ) : null}
            </span>
          )
        }
        const fullPath = tab.to.replace('$projectId', projectId)
        const active = path === fullPath
        return (
          <Link
            key={tab.key}
            to={tab.to}
            params={{ projectId }}
            role="tab"
            aria-selected={active}
            className={
              active
                ? 'px-4 py-3 text-sm font-medium border-b-2 border-brand text-brand whitespace-nowrap'
                : 'px-4 py-3 text-sm font-medium border-b-2 border-transparent text-ink-muted hover:text-ink transition-colors whitespace-nowrap'
            }
          >
            {label}
          </Link>
        )
      })}
    </nav>
  )
}

// ─── ErrorBox helper local ────────────────────────────────────────────────
function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['projects', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as const, { defaultValue: fallback })}
    </div>
  )
}

/**
 * Helper para Outlet que necesita pasar children opcionales — sin uso por
 * ahora pero puede servir si una subruta quiere wrap propio.
 */
export function ProjectOutletWrapper({ children }: { children: ReactNode }) {
  return <>{children}</>
}
